import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SubheaderService } from '../../../../core/_base/layout';
import { AppState } from '../../../../core/reducers';
import { LayoutUtilsService, QueryParamsModel } from '../../../../core/_base/crud';
import { Store, select } from '@ngrx/store';
import { debounceTime, delay, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge, of, Subscription } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';

import {
    StandardMeasurement,
    StandardMeasurementsDataSource,
    StandardMeasurementsPageRequested,
    StandardMeasurementDeleted,
    selectTrashedStandardMeasurementCount,
    selectAdminTrashedStandardMeasurementCount,
    StandardMeasurementsService,
    StandardMeasurementsActionToggleLoading,
    StandardMeasurementOnServerAdminRestored,
    StandardMeasurementOnServerRestored,
    StandardMeasurementDeletedFromTrash,
    StandardMeasurementDeletedFromAdminTrash,
    StandardMeasurementTrashFlushed,
    AssetClassType,
    AllAssetClassTypesRequested,
    selectAllAssetClassTypes,
    StandardMeasurementUpdated,
} from '../../../../core/linked-tables';
import { UntypedFormControl } from '@angular/forms';
import { addBuildingAsAssetClass } from '../lt_utils/add_building_ac';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { cloneDeep } from 'lodash';
import { hasPermission } from 'src/app/core/mad-auth/mad-auth.store';


@Component({
    selector: 'kt-standard-measurements-list',
    templateUrl: './standard-measurements-list.component.html',
    styleUrls: ['./standard-measurements-list.component.scss']
})
export class StandardMeasurementsListComponent implements OnInit, OnDestroy {

    dataSource: StandardMeasurementsDataSource;
    displayedColumns = ['id', 'name', 'asset_class_type_name', 'is_hidden', 'actions'];

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild('sort1', {static: true}) sort: MatSort;
    // Filter fields
    @ViewChild('searchInput', {static: true}) searchInput: ElementRef;
    // Selection
    selection = new SelectionModel<StandardMeasurement>(true, []);
    standardMeasurementResult: StandardMeasurement[] = [];

    trashCnt: number = 0;
    adminTrashCnt: number = 0;
    dropdownCtrl = new UntypedFormControl(7);
    public propertySubTypes: AssetClassType[] = [];

    // Subscriptions
    private subscriptions: Subscription[] = [];
    canAccessAdminTrash$ = this.store.pipe(select(hasPermission(['admin_trash'])))

    /**
     * Component constructor
     *
     * @param dialog: MatDialog
     * @param snackBar: MatSnackBar
     * @param layoutUtilsService: LayoutUtilsService
     * @param translate: TranslateService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                public dialog: MatDialog,
                public snackBar: MatSnackBar,
                public service: StandardMeasurementsService,
                public subheaderService: SubheaderService,
                private layoutUtilsService: LayoutUtilsService,
                private translate: TranslateService,
                private store: Store<AppState>) {
    }

    /**
     * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
     */

    /**
     * On init
     */
    ngOnInit() {
        this.store.dispatch(new AllAssetClassTypesRequested())
        this.store.select(selectAllAssetClassTypes).subscribe(res => {
          this.propertySubTypes = [];
          if (res) {
            this.propertySubTypes = res.filter(item => item.id == 1 || item.id == 2 || item.id == 3 || item.id == 5 || item.id == 7 || item.id == 11 || item.id == 13 || item.id == 17 || item.id == 18);
            this.propertySubTypes = addBuildingAsAssetClass(this.propertySubTypes);
          }
        });
        const dropSub = this.dropdownCtrl.valueChanges.subscribe(val => {
            this.loadStandardMeasurementsList();
        });
        this.subscriptions.push(dropSub);
        this.subheaderService.setTitle('Standard Measurements');
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Standard Measurements', page: `../admin-management/linked-tables/standard-measurements`}
        ]);

        this.paginator._intl.itemsPerPageLabel = 'Display';
        // If the user changes the sort order, reset back to the first page.
        const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.subscriptions.push(sortSubscription);

        /* Data load will be triggered in two cases:
        - when a pagination event occurs => this.paginator.page
        - when a sort event occurs => this.sort.sortChange
        **/
        const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page).pipe(
            tap(() => this.loadStandardMeasurementsList())
        )
            .subscribe();
        this.subscriptions.push(paginatorSubscriptions);

        // Filtration, bind to searchInput
        const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
            // tslint:disable-next-line:max-line-length
            debounceTime(50), // The user can type quite quickly in the input box, and that could trigger a lot of server requests. With this operator, we are limiting the amount of server requests emitted to a maximum of one every 150ms
            distinctUntilChanged(), // This operator will eliminate duplicate values
            tap(() => {
                this.paginator.pageIndex = 0;
                this.loadStandardMeasurementsList();
            })
        )
            .subscribe();
        this.subscriptions.push(searchSubscription);

        // Init DataSource
        this.dataSource = new StandardMeasurementsDataSource(this.store);
        const entitiesSubscription = this.dataSource.entitySubject.pipe(
            skip(1),
            distinctUntilChanged()
        ).subscribe(res => {
            this.standardMeasurementResult = res;
            
        });

        this.subscriptions.push(entitiesSubscription);

        // trash count
        const selectTrashedSubscription = this.store.pipe(
            select(selectTrashedStandardMeasurementCount)
        ).subscribe(res => {
            this.trashCnt = res;
        });
        this.subscriptions.push(selectTrashedSubscription);

        const selectAdminTrashedSubscription = this.store.pipe(
            select(selectAdminTrashedStandardMeasurementCount)
        ).subscribe(res => {
            this.adminTrashCnt = res;
        });
        this.subscriptions.push(selectAdminTrashedSubscription);

        // First load
        of(undefined).pipe(take(1), delay(1000)).subscribe(() => { // Remove this line, just loading imitation
            this.loadStandardMeasurementsList();
        }); // Remove this line, just loading imitation
    }

    /**
     * Load StandardMeasurements List from service through data-source
     */
    loadStandardMeasurementsList() {
        this.selection.clear();
        const queryParams = new QueryParamsModel(
            this.filterConfiguration(),
            this.sort.direction,
            this.sort.active,
            this.paginator.pageIndex + 1,
            this.paginator.pageSize
        );
        // Call request from server
        this.store.dispatch(new StandardMeasurementsPageRequested({page: queryParams}));
        this.selection.clear();
    }

    /**
     * Returns object for filter
     */
    filterConfiguration(): any {
        const filter: any = {
            with_hidden: true
        };
        const searchText: string = this.searchInput.nativeElement.value;

        filter.status = this.dropdownCtrl.value;
        filter.name = searchText;
        if (!searchText) {
            return filter;
        }
        return filter;
    }

    /**
     * Returns object for filter
     */
    resetFilter(): any {
        this.searchInput.nativeElement.value = '';
        this.loadStandardMeasurementsList();
    }

    /**
     * Show add standardMeasurement dialog
     */
    addStandardMeasurement() {
        this.editStandardMeasurement(null);
    }

    /**
     * Show Edit standardMeasurement view and save after success close result
     * @param standardMeasurement: StandardMeasurement
     */
    editStandardMeasurement(_standardMeasurement) {
        if (_standardMeasurement) {
            this.router.navigate(['edit', _standardMeasurement.id], {relativeTo: this.activatedRoute});
        } else {
            this.router.navigate(['add'], {relativeTo: this.activatedRoute});
        }
    }

    deleteStandardMeasurement(_item: StandardMeasurement) {
        const _title = this.translate.instant('STANDARD_MEASUREMENT.LIST.DIALOG.DELETE.TITLE');
        const _description: string = this.translate.instant('STANDARD_MEASUREMENT.LIST.DIALOG.DELETE.DESCRIPTION', {name: _item.name});
        const _waitDesciption =  this.translate.instant('STANDARD_MEASUREMENT.LIST.DIALOG.DELETE.WAIT_DESCRIPTION');

        const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
        dialogRef.afterClosed().subscribe(res => {
            if (!res) {
                return;
            }

            this.store.dispatch(new StandardMeasurementDeleted({id: _item.id}));
        });
    }

    /**
     * On Destroy
     */
    ngOnDestroy() {
        this.subscriptions.forEach(el => el.unsubscribe());
    }

    // Fetch trash data
    trash() {
        this.store.dispatch(new StandardMeasurementsActionToggleLoading({isLoading: true}));
        this.service.getTrashed().subscribe(res => {

            this.store.dispatch(new StandardMeasurementsActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: `${elem.name}`,
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    date: elem.user_deleted,
                    deletedUser: (elem.userDeletedBy && elem.userDeletedBy.length > 0 ? elem.userDeletedBy : ''),
                });
            });
            this.show_trash(items);
        });
    }

    // Fetch admin trash data
    adminTrash() {
        this.store.dispatch(new StandardMeasurementsActionToggleLoading({isLoading: true}));
        this.service.getAdminTrashed().subscribe(res => {

            this.store.dispatch(new StandardMeasurementsActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: `${elem.name}`,
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    date: elem.deleted_at,
                    deletedUser: (elem.deletedBy && elem.deletedBy.length > 0 ? elem.deletedBy : ''),
                });
            });
            this.show_trash(items, true);
        });
    }

    /**
     * Show Edit agency dialog and save after success close result
     * @param items
     * @param isAdmin
     */
    show_trash(items: any[], isAdmin = false) {
        let _title = this.translate.instant('STANDARD_MEASUREMENT.LIST.DIALOG.SHOW_TRASH.TITLE');
        if (isAdmin) {
            _title = this.translate.instant('STANDARD_MEASUREMENT.LIST.DIALOG.SHOW_TRASH.TITLE_ADMIN');
        }
        this.layoutUtilsService.trashedEntities(_title, items, this, isAdmin);
    }

    restore(id: number, isAdmin = false) {
        if (isAdmin) {
            this.store.dispatch(new StandardMeasurementOnServerAdminRestored({id}));
        } else {
            this.store.dispatch(new StandardMeasurementOnServerRestored({id}));
        }
    }

    delete(id: number) {
        this.store.dispatch(new StandardMeasurementDeletedFromTrash({id}));
    }

    hardDelete(id: number) {
        this.store.dispatch(new StandardMeasurementDeletedFromAdminTrash({id}));
    }

    flush() {
        
        this.store.dispatch(new StandardMeasurementTrashFlushed());
    }

    viewEntry(item: StandardMeasurement) {
        this.router.navigate(['view', item.id], {relativeTo: this.activatedRoute})
    }
    onHiddenChange(event: MatCheckboxChange, item: StandardMeasurement) {
        const _item = cloneDeep(item)
        _item.is_hidden = event.checked
        this.store.dispatch(new StandardMeasurementUpdated({
            standardMeasurement: _item,
            partialstandardMeasurement: {
                id: item.id,
                changes: {
                    is_hidden: event.checked
                }
            }
        }))
    }
}
